import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Plus, Edit, Trash2, Search, ArrowUpDown, ArrowUp, ArrowDown, X } from 'lucide-react'
import apiClient from '@/services/api/client'
import toast from 'react-hot-toast'
import { formatDateTime } from '@/lib/utils'

interface UserItem {
  id: number
  email: string
  username: string
  first_name?: string
  last_name?: string
  is_active: boolean
  is_verified: boolean
  created_at: string
  last_login?: string | null
  authorities: string[]
}

interface PaginatedUsers {
  users: UserItem[]
  total: number
  page: number
  size: number
  pages: number
  has_next: boolean
  has_prev: boolean
}

const ALL_ROLES = [
  'ROLE_USER',
  'ROLE_ADMIN',
  'ROLE_USER_ADMIN',
  'ROLE_USER_ADMIN_D',
  'ROLE_USER_ALL',
  'ROLE_USER_MRF',
] as const

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0]!.toUpperCase()).join('') || '?'
}
function avatarColorClass(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0
  return `bk-avatar-c${Math.abs(hash) % 6}`
}

export default function UsersPage() {
  const qc = useQueryClient()
  const [page, setPage] = useState(1)
  const [size] = useState(20)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [editingUser, setEditingUser] = useState<UserItem | null>(null)
  const [deletingUser, setDeletingUser] = useState<UserItem | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [sortBy, setSortBy] = useState('id')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')

  const handleSort = (column: string) => {
    if (sortBy === column) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    else { setSortBy(column); setSortOrder('asc') }
  }
  const SortIcon = ({ column }: { column: string }) => {
    if (sortBy !== column) return <ArrowUpDown size={12} />
    return sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />
  }

  const { data, isLoading } = useQuery({
    queryKey: ['admin-users', page, size, searchTerm, roleFilter, sortBy, sortOrder],
    queryFn: async (): Promise<PaginatedUsers> => {
      const { data } = await apiClient.get('/admin/users/', {
        params: { page, size, search: searchTerm || undefined, role: roleFilter || undefined, sort_by: sortBy, sort_order: sortOrder },
      })
      return data
    },
  })

  const createUserMutation = useMutation({
    mutationFn: (userData: Partial<UserItem>) => apiClient.post('/admin/users/', userData),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] })
      setShowCreateModal(false)
      toast.success('User created successfully')
    },
    onError: (error: any) => {
      const msg = error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to create user'
      toast.error(msg)
    },
  })

  const editUserMutation = useMutation({
    mutationFn: (userData: Partial<UserItem>) => apiClient.put('/admin/users/', userData),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] })
      setShowEditModal(false)
      setEditingUser(null)
      toast.success('User updated successfully')
    },
    onError: (error: any) => {
      const msg = error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to update user'
      toast.error(msg)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiClient.delete(`/admin/users/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] })
      toast.success('User deleted successfully')
    },
    onError: (error: any) => toast.error(error.response?.data?.detail || 'Failed to delete user'),
  })

  return (
    <div style={{ background: 'var(--bk-bg)', minHeight: '100%' }}>
      <section className="bk-page-hero">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 className="bk-page-title">User management</h1>
            <p className="bk-page-sub">Manage user accounts, roles and permissions.</p>
          </div>
          <button type="button" className="bk-btn bk-btn-primary bk-btn-lg" onClick={() => setShowCreateModal(true)}>
            <Plus size={16} /> Create user
          </button>
        </div>
      </section>

      <div style={{ padding: '20px 32px' }}>
        <div className="bk-toolbar">
          <div className="bk-input-wrap has-icon has-suffix" style={{ flex: 1, maxWidth: 480 }}>
            <span className="bk-input-icon"><Search size={16} /></span>
            <input
              type="text"
              className="bk-input"
              placeholder="Search users by name, email or username…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button type="button" className="bk-input-clear" onClick={() => setSearchTerm('')} aria-label="Clear search">
                <X size={14} />
              </button>
            )}
          </div>

          <select
            className="bk-select"
            value={roleFilter}
            onChange={e => setRoleFilter(e.target.value)}
            style={{ height: 42, minWidth: 200 }}
          >
            <option value="">All roles</option>
            {ALL_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>

          {(searchTerm || roleFilter) && (
            <button type="button" className="bk-btn bk-btn-ghost" onClick={() => { setSearchTerm(''); setRoleFilter('') }}>
              Clear
            </button>
          )}

          <div style={{ flex: 1 }} />

          <span className="bk-badge bk-badge-outline">{data?.total ?? 0} users</span>
        </div>
      </div>

      <div style={{ padding: '0 32px 64px' }}>
        <div className="bk-card">
          {isLoading ? (
            <div style={{ padding: 24, display: 'grid', gap: 12 }}>
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="bk-skel" style={{ height: 56 }} />)}
            </div>
          ) : !data || data.users.length === 0 ? (
            <div className="bk-empty">
              <div className="icon"><Search size={22} /></div>
              <div className="ttl">No users</div>
              <div className="sub">
                {searchTerm || roleFilter
                  ? 'Try adjusting your search or role filter.'
                  : 'No users in the system yet.'}
              </div>
              {(searchTerm || roleFilter) && (
                <button type="button" className="bk-btn bk-btn-secondary" onClick={() => { setSearchTerm(''); setRoleFilter('') }}>
                  Reset filters
                </button>
              )}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="bk-tbl">
                <thead>
                  <tr>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('first_name')}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>Name <SortIcon column="first_name" /></span>
                    </th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('email')}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>Email <SortIcon column="email" /></span>
                    </th>
                    <th>Roles</th>
                    <th style={{ cursor: 'pointer' }} className="bk-hide-mobile" onClick={() => handleSort('last_login')}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>Last login <SortIcon column="last_login" /></span>
                    </th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.users.map(u => {
                    const name = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email
                    return (
                      <tr key={u.id}>
                        <td>
                          <div className="name-cell">
                            <div className={`bk-avatar bk-avatar-md ${avatarColorClass(name)}`}>{initials(name)}</div>
                            <div style={{ minWidth: 0 }}>
                              <div className="name">{name}</div>
                              {u.is_active
                                ? <div className="email"><span className="bk-badge bk-badge-success bk-badge-dot" style={{ height: 18, padding: '0 8px', fontSize: 10 }}>Active</span></div>
                                : <div className="email"><span className="bk-badge bk-badge-soft bk-badge-dot" style={{ height: 18, padding: '0 8px', fontSize: 10 }}>Inactive</span></div>}
                            </div>
                          </div>
                        </td>
                        <td style={{ color: 'var(--bk-ink-2)' }}>{u.email}</td>
                        <td>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {(u.authorities || []).map((role, i) => (
                              <span key={i} className="bk-badge bk-badge-accent bk-mono" style={{ fontSize: 10.5, height: 22, padding: '0 8px' }}>
                                {typeof role === 'string' ? role : (role as any)?.name || String(role)}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="bk-hide-mobile" style={{ color: 'var(--bk-muted)', fontSize: 13 }}>
                          {u.last_login ? formatDateTime(u.last_login) : 'Never'}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <div style={{ display: 'inline-flex', gap: 6 }}>
                            <button
                              type="button"
                              className="bk-btn bk-btn-secondary bk-btn-sm"
                              onClick={() => { setEditingUser(u); setShowEditModal(true) }}
                              aria-label="Edit user"
                            >
                              <Edit size={14} /> Edit
                            </button>
                            <button
                              type="button"
                              className="bk-btn bk-btn-danger bk-btn-sm"
                              onClick={() => { setDeletingUser(u); setShowDeleteModal(true) }}
                              aria-label="Delete user"
                            >
                              <Trash2 size={14} /> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!isLoading && data && data.pages > 1 && (
            <div style={{ padding: '14px 22px', borderTop: '1px solid var(--bk-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                <button
                  className="bk-btn bk-btn-secondary bk-btn-sm"
                  disabled={page <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <span style={{ fontSize: 13, color: 'var(--bk-muted)' }}>
                  Page <strong style={{ color: 'var(--bk-ink)' }}>{page}</strong> of <strong style={{ color: 'var(--bk-ink)' }}>{data.pages || 1}</strong>
                </span>
                <button
                  className="bk-btn bk-btn-secondary bk-btn-sm"
                  disabled={page >= (data.pages || 1)}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </button>
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--bk-muted)' }}>
                Showing {((page - 1) * size) + 1}–{Math.min(page * size, data.total)} of {data.total} users
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create user modal */}
      {showCreateModal && (
        <div className="bk-modal-backdrop" onClick={() => setShowCreateModal(false)}>
          <div className="bk-modal bk-modal-lg" onClick={e => e.stopPropagation()}>
            <header className="bk-modal-hd">
              <div className="ttl">Create user</div>
              <button className="bk-icon-btn" onClick={() => setShowCreateModal(false)} aria-label="Close"><X size={16} /></button>
            </header>
            <form
              onSubmit={e => {
                e.preventDefault()
                const fd = new FormData(e.currentTarget)
                const selectedRoles = fd.getAll('authorities') as string[]
                const email = fd.get('email') as string
                // OAuth-only deployment: derive `login` from the email prefix
                // (matches the backend's loginFromEmail used by Google sign-in).
                const loginFromEmail = (email.split('@')[0] || '').toLowerCase().slice(0, 100)
                createUserMutation.mutate({
                  login: loginFromEmail,
                  email,
                  first_name: fd.get('first_name') as string,
                  last_name: fd.get('last_name') as string,
                  authorities: selectedRoles.length > 0 ? selectedRoles : ['ROLE_USER'],
                  is_active: true,
                } as Partial<UserItem>)
              }}
            >
              <div className="bk-modal-bd">
                <div className="bk-modal-grid">
                  <div>
                    <label className="bk-label">First name *</label>
                    <input name="first_name" type="text" className="bk-input" required />
                  </div>
                  <div>
                    <label className="bk-label">Last name *</label>
                    <input name="last_name" type="text" className="bk-input" required />
                  </div>
                  <div className="full">
                    <label className="bk-label">Email *</label>
                    <input name="email" type="email" className="bk-input" required />
                  </div>
                  <div className="full">
                    <label className="bk-label">Roles *</label>
                    <select name="authorities" multiple required className="bk-select" style={{ height: 128, padding: 8, backgroundImage: 'none' }}>
                      {ALL_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <p style={{ fontSize: 12, color: 'var(--bk-muted)', marginTop: 6 }}>Hold Ctrl / Cmd to select multiple.</p>
                  </div>
                </div>
              </div>
              <footer className="bk-modal-ft">
                <button type="button" className="bk-btn bk-btn-ghost" onClick={() => setShowCreateModal(false)}>Cancel</button>
                <button type="submit" className="bk-btn bk-btn-primary" disabled={createUserMutation.isPending}>
                  {createUserMutation.isPending ? 'Creating…' : 'Create user'}
                </button>
              </footer>
            </form>
          </div>
        </div>
      )}

      {/* Edit user modal */}
      {showEditModal && editingUser && (
        <div className="bk-modal-backdrop" onClick={() => { setShowEditModal(false); setEditingUser(null) }}>
          <div className="bk-modal bk-modal-lg" onClick={e => e.stopPropagation()}>
            <header className="bk-modal-hd">
              <div className="ttl">Edit user</div>
              <button className="bk-icon-btn" onClick={() => { setShowEditModal(false); setEditingUser(null) }} aria-label="Close"><X size={16} /></button>
            </header>
            <form
              onSubmit={e => {
                e.preventDefault()
                const fd = new FormData(e.currentTarget)
                const selectedRoles = fd.getAll('authorities') as string[]
                const email = fd.get('email') as string
                editUserMutation.mutate({
                  id: editingUser.id,
                  username: email,
                  email,
                  first_name: fd.get('first_name') as string,
                  last_name: fd.get('last_name') as string,
                  authorities: selectedRoles.length > 0 ? selectedRoles : ['ROLE_USER'],
                } as Partial<UserItem>)
              }}
            >
              <div className="bk-modal-bd">
                <div className="bk-modal-grid">
                  <div>
                    <label className="bk-label">First name *</label>
                    <input name="first_name" type="text" className="bk-input" defaultValue={editingUser.first_name} required />
                  </div>
                  <div>
                    <label className="bk-label">Last name *</label>
                    <input name="last_name" type="text" className="bk-input" defaultValue={editingUser.last_name} required />
                  </div>
                  <div className="full">
                    <label className="bk-label">Email *</label>
                    <input name="email" type="email" className="bk-input" defaultValue={editingUser.email} required />
                  </div>
                  <div className="full">
                    <label className="bk-label">Roles *</label>
                    <select name="authorities" multiple required className="bk-select" style={{ height: 128, padding: 8, backgroundImage: 'none' }}>
                      {ALL_ROLES.map(r => (
                        <option key={r} value={r} selected={editingUser.authorities.includes(r)}>{r}</option>
                      ))}
                    </select>
                    <p style={{ fontSize: 12, color: 'var(--bk-muted)', marginTop: 6 }}>Hold Ctrl / Cmd to select multiple.</p>
                  </div>
                </div>
              </div>
              <footer className="bk-modal-ft">
                <button type="button" className="bk-btn bk-btn-ghost" onClick={() => { setShowEditModal(false); setEditingUser(null) }}>Cancel</button>
                <button type="submit" className="bk-btn bk-btn-primary" disabled={editUserMutation.isPending}>
                  {editUserMutation.isPending ? 'Updating…' : 'Update user'}
                </button>
              </footer>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteModal && deletingUser && (
        <div className="bk-modal-backdrop" onClick={() => { setShowDeleteModal(false); setDeletingUser(null) }}>
          <div className="bk-modal" onClick={e => e.stopPropagation()}>
            <header className="bk-modal-hd">
              <div className="ttl">Confirm delete</div>
              <button className="bk-icon-btn" onClick={() => { setShowDeleteModal(false); setDeletingUser(null) }} aria-label="Close"><X size={16} /></button>
            </header>
            <div className="bk-modal-bd">
              <p style={{ color: 'var(--bk-ink-2)', marginBottom: 14 }}>
                Are you sure you want to delete this user? This cannot be undone.
              </p>
              <div style={{ background: 'var(--bk-bg-soft)', borderRadius: 'var(--bk-radius-md)', padding: 14 }}>
                <div style={{ fontWeight: 600, color: 'var(--bk-ink)' }}>
                  {deletingUser.first_name} {deletingUser.last_name}
                </div>
                <div style={{ fontSize: 13, color: 'var(--bk-ink-2)' }}>{deletingUser.email}</div>
                <div style={{ fontSize: 12.5, color: 'var(--bk-muted)', marginTop: 4 }}>
                  Roles: {deletingUser.authorities?.map(role => typeof role === 'string' ? role : (role as any)?.name || String(role)).join(', ') || 'None'}
                </div>
              </div>
            </div>
            <footer className="bk-modal-ft">
              <button type="button" className="bk-btn bk-btn-ghost" onClick={() => { setShowDeleteModal(false); setDeletingUser(null) }}>Cancel</button>
              <button
                type="button"
                className="bk-btn bk-btn-danger"
                disabled={deleteMutation.isPending}
                onClick={() => {
                  deleteMutation.mutate(deletingUser.id)
                  setShowDeleteModal(false)
                  setDeletingUser(null)
                }}
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete user'}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  )
}
